import React, { useState, useRef, useEffect } from 'react'

import { useDarkMode } from '../../context/DarkModeContext'

const capitalName = str => {
	return str
		?.toLowerCase()
		?.split(' ')
		?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		?.join(' ')
}

const formatProperCase = str => {
	if (!str) return str
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const SelectedFilters = ({ filters, initialFilters, removeFilter, clearAllFilters }) => {
	const { isDarkMode } = useDarkMode()
	const [confirmingClear, setConfirmingClear] = useState(false)
	const clearTimeoutRef = useRef(null)

	useEffect(() => {
		return () => {
			if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current)
		}
	}, [])

	const handleClearClick = () => {
		if (!confirmingClear) {
			setConfirmingClear(true)
			clearTimeoutRef.current = setTimeout(() => setConfirmingClear(false), 4000)
			return
		}
		if (clearTimeoutRef.current) clearTimeout(clearTimeoutRef.current)
		setConfirmingClear(false)
		clearAllFilters()
	}

	const chipClass =
		'flex items-center rounded-full bg-cyan-500 py-0.5 pl-2 pr-1 text-white'
	const chipTextClass = 'text-[11px] leading-4'
	const chipRemoveClass =
		'ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[11px] font-semibold leading-none text-gray-500 transition-colors hover:bg-white/20 hover:text-gray-700'

	// Function to remove selected city from filters
	const removeSelectedCity = (locationId, type) => {
		const filterKey = type === 'origin' ? 'pickupLocations' : 'dropoffLocations'
		removeFilter(filterKey, locationId)
	}

	// Function to render selected cities
	const renderSelectedCities = (locations, type) => {
		if (!locations || locations.length === 0) return null

		return locations.map(location => (
			<div
				key={`${type}-${location.name}`}
				className={chipClass}
			>
				<span
					className={chipTextClass}
				>{`${type === 'origin' ? 'Origin' : 'Destination'}: ${location.name}`}</span>
				<button
					type="button"
					className={chipRemoveClass}
					onClick={() => removeSelectedCity(location.name, type)}
					aria-label={`Remove ${type} filter`}
				>
					x
				</button>
			</div>
		))
	}

	const keyNameMapping = {
		distance: 'Min Distance',
		driverType: 'Driver Type',
		equipType: 'Equipment',
		stopCount: 'Max Stops',
		weight: 'Max Weight',
		ignoreLoadOut: 'Loadout',
		isAIEnabled: 'AI Bookable',
		loadType: 'Load Type',
		workType: 'Work Type',
		certRequired: 'Certification',
		maxdistance: 'Max Distance',
		price: 'Payout',
		pricePerMile: 'Per Mile',
		carrierGrades: 'Performance Score',
		priceThreshold: 'Price Threshold',
		destinationStates: 'Destination States',
		destinationRegions: 'Destination Regions',
		pickupDateFrom: 'Pickup From',
		deliveryDateTo: 'Delivery To',
	}

	const formatDateValue = isoString => {
		const date = new Date(isoString)
		if (isNaN(date.getTime())) return isoString
		const mm = String(date.getMonth() + 1).padStart(2, '0')
		const dd = String(date.getDate()).padStart(2, '0')
		const yyyy = date.getFullYear()
		const hh = String(date.getHours()).padStart(2, '0')
		const min = String(date.getMinutes()).padStart(2, '0')
		return `${mm}/${dd}/${yyyy} ${hh}:${min}`
	}

	const getDisplayName = (key, value) => {
		const displayName = keyNameMapping[key] || key
		if (key === 'loadType') {
			return `${displayName}: ${value === 'Drop' ? 'Drop & Hook' : value}`
		}
		if (key === 'workType') {
			return `${displayName}: ${value === 'OneWay' ? 'One Way' : 'Round Trip'}`
		}
		if (key === 'certRequired') {
			return `${displayName}: ${value === 'required' ? 'Required' : 'Not Required'}`
		}
		if (key === 'pickupDateFrom' || key === 'deliveryDateTo') {
			return `${displayName}: ${formatDateValue(value)}`
		}
		if (key === 'equipType') {
			return `${displayName}: ${capitalName(value)}`
		}
		if (key === 'carrierGrades' && Array.isArray(value) && value.length > 0) {
			return `${displayName}: ${value
				.map(grade => grade.replace('_PLUS', '+'))
				.join(', ')}`
		}
		if (key === 'destinationStates' && Array.isArray(value) && value.length > 0) {
			return `${displayName}: ${value.join(', ')}`
		}
		if (key === 'destinationRegions' && Array.isArray(value) && value.length > 0) {
			return `${displayName}: ${value.join(', ')}`
		}
		return `${displayName}: ${value}`
	}


	const renderSourceTypes = () => {
		const sourceTypes = filters['sourceType']
		if (sourceTypes && Array.isArray(sourceTypes) && sourceTypes.length > 0) {
			return sourceTypes.map((source, index) => (
				<div
					key={index}
					className={chipClass}
				>
					<span
						className={chipTextClass}
					>{`Source: ${source.label}`}</span>
					<button
						type="button"
						className={chipRemoveClass}
						onClick={() => removeFilter('sourceType', index)}
						aria-label="Remove source filter"
					>
						x
					</button>
				</div>
			))
		}
		return null
	}

	const renderDestinationStates = () => {
		const destinationStates = filters['destinationStates']
		if (
			destinationStates &&
			Array.isArray(destinationStates) &&
			destinationStates.length > 0
		) {
			return (
				<div
					key="destination-states-all"
					className={chipClass}
				>
					<span
						className={chipTextClass}
					>{`Destination States: ${destinationStates.join(', ')}`}</span>
					<button
						type="button"
						className={chipRemoveClass}
						onClick={() => removeFilter('destinationStates', 'all')}
						aria-label="Remove destination states filter"
					>
						x
					</button>
				</div>
			)
		}
		return null
	}

	const renderDestinationRegions = () => {
		const destinationRegions = filters['destinationRegions']
		if (
			destinationRegions &&
			Array.isArray(destinationRegions) &&
			destinationRegions.length > 0
		) {
			return (
				<div
					key="destination-regions-all"
					className={chipClass}
				>
					<span
						className={chipTextClass}
					>{`Destination Regions: ${destinationRegions.map(region => formatProperCase(region)).join(', ')}`}</span>
					<button
						type="button"
						className={chipRemoveClass}
						onClick={() => removeFilter('destinationRegions', 'all')}
						aria-label="Remove destination regions filter"
					>
						x
					</button>
				</div>
			)
		}
		return null
	}

	const otherChips = Object.keys(filters).map(key => {
		if (
			filters[key] &&
			filters[key] !== initialFilters[key] &&
			![
				'pickupCity',
				'pickupState',
				'pickupLat',
				'pickupLng',
				'origin',
				'pickupStateName',
				'dropCity',
				'dropState',
				'dropLat',
				'dropLng',
				'dropStateName',
				'destination',
				'sourceType',
				'dropoffMaxDist',
				'pickupMaxDist',
				'limited',
				'paginateSize',
				'sortBy',
				'carrierGrade',
				'moveResultsToTop',
				'pickupLocations',
				'dropoffLocations',
				'destinationStates',
				'destinationRegions',
				'destinationType',
				'lastSeen',
			].includes(key) &&
			(key !== 'carrierGrades' ||
				(Array.isArray(filters[key]) && filters[key].length > 0))
		) {
			return (
				<div
					key={key}
					className={chipClass}
				>
					<span className={chipTextClass}>
						{getDisplayName(key, filters[key])}
					</span>
					<button
						type="button"
						className={chipRemoveClass}
						onClick={() => removeFilter(key)}
						aria-label={`Remove ${key} filter`}
					>
						x
					</button>
				</div>
			)
		}
		return null
	})

	const chips = [
		...(renderSelectedCities(filters.pickupLocations, 'origin') || []),
		...(renderSelectedCities(filters.dropoffLocations, 'destination') || []),
		renderDestinationStates(),
		renderDestinationRegions(),
		...(renderSourceTypes() || []),
		...otherChips,
	].filter(Boolean)

	useEffect(() => {
		if (chips.length === 0 && confirmingClear) {
			setConfirmingClear(false)
		}
	}, [chips.length, confirmingClear])

	return (
		<div className="ml-0 flex flex-wrap items-center gap-1.5">
			{chips}
			{chips.length > 0 && (
				<button
					type="button"
					onClick={handleClearClick}
					className={`flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 transition-colors ${
						confirmingClear
							? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
							: isDarkMode
								? 'border-gray-600 text-gray-300 hover:bg-gray-700'
								: 'border-gray-300 text-gray-600 hover:bg-gray-100'
					}`}
					aria-label={confirmingClear ? 'Confirm clear all filters' : 'Clear all filters'}
				>
					{confirmingClear ? 'Confirm?' : 'Clear'}
				</button>
			)}
		</div>
	)
}

export default SelectedFilters
